import type { ConversationMessage } from '@chronosphereio/ai';
import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useRef, useState } from 'react';

type QueryAssistantProviderProps = {
  children: ReactNode;
  query: string;
  onChange: (query: string) => void;
  onRun?: () => void;
  onOpen?: () => void;
};

export type QuerySuggestion = {
  query: string;
  metadata?: { version?: string; input?: string; existingQuery?: string };
};

type QueryAssistantContextType = {
  isAssistantOpen: boolean;
  openAssistant: () => void;
  closeAssistant: () => void;
  query: string;
  /**
   * The user's baseline query.
   * This tracks `query`, then freezes when there is a suggestion.
   * We show a diff against `committedQuery` and we revert to it if user rejects the suggestion.
   */
  committedQuery: string;
  onChange: (query: string) => void;
  inputValue: string;
  setInputValue: (input: string) => void;
  hasInputValue: boolean;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  suggestion: QuerySuggestion | null;
  setSuggestion: (suggestion: QuerySuggestion | null) => void;
  previousPrompt: string | null;
  setPreviousPrompt: (prompt: string | null) => void;
  history: ConversationMessage[];
  setHistory: Dispatch<SetStateAction<ConversationMessage[]>>;
  errorMessage: string | null;
  setErrorMessage: (errorMessage: string | null) => void;
  handleCancel: () => void;
  handleAcceptAndRun: () => void;
  handleAccept: () => void;
  handleReject: () => void;
  cancelAndSetAbortController: (controller: AbortController | null) => void;
};

const QueryAssistantContext = createContext<QueryAssistantContextType | undefined>(undefined);

export function QueryAssistantProvider(props: QueryAssistantProviderProps) {
  const { children, query, onChange, onRun, onOpen } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<QuerySuggestion | null>(null);
  const [previousPrompt, setPreviousPrompt] = useState<string | null>(null);
  const [history, setHistory] = useState<ConversationMessage[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const cancelAndSetAbortController = useCallback((controller: AbortController | null) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = controller;
  }, []);

  // Keep `committedQuery` in sync with `query` when there is no suggestion.
  // When there is a suggestion, `committedQuery` is frozen.
  const [committedQuery, setCommittedQuery] = useState(query);
  if (!suggestion && query !== committedQuery) {
    setCommittedQuery(query);
  }

  const hasInputValue = Boolean(inputValue.trim());

  const handleOpen = useCallback(() => {
    onOpen?.();
    setIsOpen(true);
  }, [onOpen]);

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
  }, []);

  const handleAccept = useCallback(() => {
    setSuggestion(null);
  }, []);

  const handleAcceptAndRun = useCallback(() => {
    setSuggestion(null);
    onRun?.();
  }, [onRun]);

  const handleReject = useCallback(() => {
    onChange(committedQuery);
    setSuggestion(null);
  }, [onChange, committedQuery]);

  const handleClose = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
    setIsOpen(false);
    setInputValue('');
    setPreviousPrompt(null);
    setHistory([]);
    setErrorMessage(null);
    handleReject();
  }, [handleReject]);

  return (
    <QueryAssistantContext.Provider
      value={{
        isAssistantOpen: isOpen,
        openAssistant: handleOpen,
        closeAssistant: handleClose,
        query,
        committedQuery,
        onChange,
        inputValue,
        setInputValue,
        hasInputValue,
        isLoading,
        setIsLoading,
        suggestion,
        setSuggestion,
        previousPrompt,
        setPreviousPrompt,
        history,
        setHistory,
        errorMessage,
        setErrorMessage,
        handleCancel,
        handleAcceptAndRun,
        handleAccept,
        handleReject,
        cancelAndSetAbortController,
      }}
    >
      {children}
    </QueryAssistantContext.Provider>
  );
}

export function useQueryAssistant() {
  const context = useContext(QueryAssistantContext);
  if (context === undefined) {
    throw new Error('useQueryAssistant must be used within a QueryAssistantProvider');
  }
  return context;
}
